/**
 * Typed API client for the external-auditor audit-log export
 * (GET /admin/audit-log/export). super_admin only; the call is itself recorded
 * in the audit log (AUDIT_LOG_EXPORTED) before any byte is sent.
 */
import axios, { type AxiosInstance } from 'axios';
import authService from './auth.service';

export type ExportFormat = 'csv' | 'json';

export interface AuditExportFilters {
  /** Inclusive UTC day, YYYY-MM-DD. */
  from: string;
  /** Inclusive UTC day, YYYY-MM-DD. */
  to: string;
  actorId?: string;
  action?: string;
  targetType?: string;
  targetId?: string;
}

export interface AuditExportFile {
  blob: Blob;
  filename: string;
}

/** Query string the API expects: omitted filters are not sent. */
export function buildExportParams(
  filters: AuditExportFilters,
  format: ExportFormat,
): Record<string, string> {
  const params: Record<string, string> = { from: filters.from, to: filters.to, format };
  if (filters.actorId) params.actor_id = filters.actorId;
  if (filters.action) params.action = filters.action;
  if (filters.targetType) params.target_type = filters.targetType;
  if (filters.targetId) params.target_id = filters.targetId;
  return params;
}

/** The file name the server proposes, or the same name built from the range. */
export function exportFilename(
  disposition: string | undefined,
  filters: AuditExportFilters,
  format: ExportFormat,
): string {
  const named = /filename="([^"\\/]+)"/.exec(disposition ?? '');
  return named?.[1] ?? `audit-log-${filters.from}-${filters.to}.${format}`;
}

/**
 * A blob response carries its error body as a Blob, which hides the API's
 * `detail` from the shared error reader. Turn it back into the parsed body.
 */
async function unwrapBlobError(err: unknown): Promise<unknown> {
  if (axios.isAxiosError(err) && err.response?.data instanceof Blob) {
    try {
      err.response.data = JSON.parse(await err.response.data.text());
    } catch {
      err.response.data = {};
    }
  }
  return err;
}

class AuditLogExportService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async exportLog(filters: AuditExportFilters, format: ExportFormat): Promise<AuditExportFile> {
    try {
      const res = await this.api.get<Blob>('/api/v1/admin/audit-log/export', {
        params: buildExportParams(filters, format),
        responseType: 'blob',
      });
      const disposition = res.headers?.['content-disposition'] as string | undefined;
      return { blob: res.data, filename: exportFilename(disposition, filters, format) };
    } catch (err: unknown) {
      throw await unwrapBlobError(err);
    }
  }
}

export default new AuditLogExportService();
